import React, { useState } from 'react';
import { useEditMode } from '../../context/EditModeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';

function FloatingSaveButton() {
    const { isEditMode, saveChanges, toggleEditMode, setEditModeEnabled, isSaving, isAuthenticated, logout } = useEditMode();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    if (!isEditMode) {
        return null;
    }

    const handleSave = async () => {
        try {
            // If on variant page with custom save handler, use it; otherwise fallback to content save
            if ((window as any).customSaveVariant) {
                await (window as any).customSaveVariant();
            } else {
                await saveChanges();
            }
            setShowSuccessModal(true);
            setTimeout(() => {
                setShowSuccessModal(false);
            }, 3000);
        } catch (error) {
            console.error('Error saving changes:', error);
        }
    };

    const handleExit = () => {
        // Update URL to remove edit parameter
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('edit');
        const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

        // Explicitly set edit mode to false before navigation
        setEditModeEnabled(false);
        navigate(newUrl);
    };

    const handleLogout = async () => {
        await logout();
        setShowLogoutConfirm(false);
        // Navigate away from edit mode
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('edit');
        const newUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
        navigate(newUrl);
    };

    // Get language display name
    const getLanguageDisplay = () => {
        switch (language) {
            case 'en': return 'English';
            case 'de': return 'German';
            case 'zh': return 'Chinese';
            case 'fr': return 'French';
            default: return String(language).toUpperCase();
        }
    };

    return (
        <>
            <div className="fixed bottom-8 right-8 z-50">
                <div className="flex flex-col items-end">
                    <div className="flex space-x-2">
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-5 rounded-lg shadow-lg flex items-center transition-all duration-300 disabled:opacity-50"
                        >
                            {isSaving ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                    </svg>
                                    <span>Save</span>
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleExit}
                            className="bg-white hover:bg-gray-200 text-gray-600 font-bold py-2.5 px-5 rounded-lg shadow-sm flex items-center transition-all duration-300"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Exit</span>
                        </button>
                        {isAuthenticated && (
                            <button
                                onClick={() => setShowLogoutConfirm(true)}
                                className="bg-gray-100 hover:bg-gray-200 text-gray-500 py-2 px-3 rounded-lg shadow-sm flex items-center transition-all duration-300 text-sm"
                                title='Logout'
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md z-50 animate-fade-in-down">
                    <div className="flex items-center">
                        <svg className="h-6 w-6 text-green-500 mr-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <p>Changes saved successfully for {getLanguageDisplay()}!</p>
                    </div>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Confirm Logout</h2>
                            <button 
                                onClick={() => setShowLogoutConfirm(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>
                        
                        <p className="mb-6 text-gray-600">
                            Are you sure you want to log out? Any unsaved changes will be lost.
                        </p>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLogout}
                                className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default FloatingSaveButton; 